/**
 * Statuslinje nederst i editoren.
 * Venstre: valgt element (reol / flervalg / enkeltlokasjon) med posisjon, mål og rotasjon.
 * Høyre: lagringsstatus (lagrer / lagret kl. HH:MM / feilet).
 */

import React from 'react';
import { Box, MapPin, MousePointer2, Shapes, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { objectTypeLabel } from '../utils/objectTools';

const Dot: React.FC = () => <span className="text-gray-300">&bull;</span>;

const SelectionStatusBar: React.FC = () => {
  const { state, rackDimensionsMap } = useLayoutEditor();
  const { t } = useTranslation();
  const { layout, editorState, selectedBinCode, saveStatus, lastSavedAt } = state;

  if (!layout) return null;

  const selectedCount = editorState.selectedRacks.size;

  const renderSelection = () => {
    // Objekt valgt (søyle/port/stasjon/lading/sonemarkør)
    const selectedObject = editorState.selectedObject
      ? layout.objects.find(o => o.id === editorState.selectedObject)
      : undefined;
    if (selectedObject) {
      return (
        <>
          <Shapes className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <span className="font-medium">
            {selectedObject.name || objectTypeLabel(t, selectedObject.type)}
          </span>
          <Dot />
          <span className="text-gray-600">{objectTypeLabel(t, selectedObject.type)}</span>
          <Dot />
          <span className="text-gray-600">
            {t('manager.modules.warehouseLayout.selection.position', { '0': selectedObject.x.toFixed(1), '1': selectedObject.y.toFixed(1) }, `Posisjon: (${selectedObject.x.toFixed(1)}m, ${selectedObject.y.toFixed(1)}m)`)}
          </span>
          <Dot />
          <span className="text-gray-600">
            {t('manager.modules.warehouseLayout.selection.size', { '0': selectedObject.width.toFixed(1), '1': selectedObject.depth.toFixed(1), '2': (selectedObject.height ?? 0).toFixed(1) }, `Størrelse: ${selectedObject.width.toFixed(1)}m × ${selectedObject.depth.toFixed(1)}m × ${(selectedObject.height ?? 0).toFixed(1)}m`)}
          </span>
        </>
      );
    }

    // Enkeltlokasjon valgt
    if (selectedBinCode) {
      const pos = state.binPositions.find(p => p.binCode === selectedBinCode);
      return (
        <>
          <MapPin className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <span className="font-medium">
            {t('manager.modules.warehouseLayout.selection.binLabel', { '0': selectedBinCode }, `Lokasjon ${selectedBinCode}`)}
          </span>
          {pos && (
            <>
              <Dot />
              <span className="text-gray-600">
                {t('manager.modules.warehouseLayout.selection.position', { '0': pos.positionX.toFixed(1), '1': pos.positionY.toFixed(1) }, `Posisjon: (${pos.positionX.toFixed(1)}m, ${pos.positionY.toFixed(1)}m)`)}
              </span>
              <Dot />
              <span className="text-gray-600">
                {t('manager.modules.warehouseLayout.selection.rotation', { '0': pos.rotation }, `Rotasjon: ${pos.rotation}°`)}
              </span>
            </>
          )}
        </>
      );
    }

    // Én reol valgt
    if (selectedCount === 1) {
      const rackNumber = Array.from(editorState.selectedRacks)[0];
      const rack = layout.racks.find(r => r.rackNumber === rackNumber);
      const dim = rackDimensionsMap.get(rackNumber);
      return (
        <>
          <Box className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <span className="font-medium">
            {t('manager.modules.warehouseLayout.placement.rackLabel', { '0': rackNumber }, `Reol ${rackNumber}`)}
          </span>
          {rack && (
            <>
              <Dot />
              <span className="text-gray-600">
                {t('manager.modules.warehouseLayout.selection.position', { '0': rack.x.toFixed(1), '1': rack.y.toFixed(1) }, `Posisjon: (${rack.x.toFixed(1)}m, ${rack.y.toFixed(1)}m)`)}
              </span>
              {dim && (
                <>
                  <Dot />
                  <span className="text-gray-600">
                    {t('manager.modules.warehouseLayout.selection.size', { '0': dim.width.toFixed(1), '1': dim.depth.toFixed(1), '2': dim.height.toFixed(1) }, `Størrelse: ${dim.width.toFixed(1)}m × ${dim.depth.toFixed(1)}m × ${dim.height.toFixed(1)}m`)}
                  </span>
                </>
              )}
              <Dot />
              <span className="text-gray-600">
                {t('manager.modules.warehouseLayout.selection.rotation', { '0': rack.rotation }, `Rotasjon: ${rack.rotation}°`)}
              </span>
            </>
          )}
        </>
      );
    }

    // Flere reoler valgt
    if (selectedCount > 1) {
      return (
        <>
          <Box className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <span className="font-medium">
            {t('manager.modules.warehouseLayout.selection.racksSelected', { '0': selectedCount }, `${selectedCount} reoler valgt`)}
          </span>
          <Dot />
          <span className="text-gray-600">
            {t('manager.modules.warehouseLayout.selection.multiHint', {}, 'Trykk R for å rotere alle, Delete for å slette')}
          </span>
        </>
      );
    }

    // Ingenting valgt
    return (
      <>
        <MousePointer2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className="text-gray-400">
          {t('manager.modules.warehouseLayout.selection.noSelection', {}, 'Ingen elementer valgt — klikk en reol eller lokasjon i 2D-visningen')}
        </span>
      </>
    );
  };

  const renderSaveStatus = () => {
    if (saveStatus === 'saving') {
      return (
        <span className="flex items-center gap-1.5 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('common.status.saving', {}, 'Lagrer...')}
        </span>
      );
    }
    if (saveStatus === 'error') {
      return (
        <span className="flex items-center gap-1.5 text-red-600">
          <AlertCircle className="w-4 h-4" />
          {t('manager.modules.warehouseLayout.selection.saveFailed', {}, 'Lagring feilet')}
        </span>
      );
    }
    if (lastSavedAt) {
      const time = new Date(lastSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return (
        <span className="flex items-center gap-1.5 text-green-600">
          <CheckCircle className="w-4 h-4" />
          {t('manager.modules.warehouseLayout.selection.savedAt', { '0': time }, `Lagret ${time}`)}
        </span>
      );
    }
    return (
      <span className="text-gray-400">
        {t('manager.modules.warehouseLayout.selection.notSavedYet', {}, 'Ikke lagret ennå')}
      </span>
    );
  };

  return (
    <div className="bg-white border-t border-gray-200 px-4 py-1.5 flex items-center gap-3 text-sm flex-shrink-0">
      <div className="flex items-center gap-3 min-w-0 truncate">{renderSelection()}</div>
      <div className="ml-auto flex-shrink-0 text-xs">{renderSaveStatus()}</div>
    </div>
  );
};

export default SelectionStatusBar;
